import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Beaker, Building2, Calendar, ChevronRight, Droplets,
  CheckCircle2, Info, ArrowLeft, Search, Plus,
  Shield, ClipboardCheck, AlertTriangle, XCircle,
  CheckSquare, Square, ChevronDown, ChevronUp,
  FileText, Lock, Unlock, SlidersHorizontal,
  Clock, User, Award, History, Printer,
  Download, Eye, MapPin, X,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import PageHeader from '@/components/PageHeader';
import { hasPhpBackend, apiListInfluentSamples, apiListCustomers, apiListCustomerSites } from '@/lib/phpApi';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { generateEffluentSamplePDF, buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import { ALL_STANDARDS } from '@/types/standards';
import { useSession } from '@/context/SessionContext';
import RegulatoryNotice from '@/components/RegulatoryNotice';
import { toast } from 'sonner';

// ─── Types ─────────────────────────────────────────────────────────────────────
interface Sample {
  id: number;
  customerId: number | null;
  siteId?: number | null;
  sampleName: string;
  reportNumber: string;
  params: Record<string, number>;
  standard: string;
  sampleType: 'influent' | 'effluent';
  linkedSampleId?: number | null;
  createdAt: string;
}

interface Customer {
  id: number;
  name: string;
  company?: string;
}

type GateStatus = 'complete' | 'incomplete' | 'na';
type GateJurisdiction = 'AU' | 'UK' | 'EU' | 'US' | 'ALL';

interface DischargeGate {
  id: string;
  title: string;
  description: string;
  reference: string;
  jurisdiction: GateJurisdiction;
  required: boolean;
  status: GateStatus;
  notes: string;
  completedAt?: string;
  // Operator identity (audit trail)
  operator?: string;
  operatorId?: string;
  operatorTimestamp?: string;
}

interface AuditEntry {
  gateId: string;
  gateTitle: string;
  storageKey: string;
  fromStatus: GateStatus;
  toStatus: GateStatus;
  operator: string;
  operatorId: string;
  timestamp: string;
}

const LS_GATES_KEY = 'rb_discharge_gates_v1';
const LS_AUDIT_KEY = 'rb_discharge_gate_audit_v1';

function appendAuditEntry(entry: AuditEntry) {
  try {
    const raw = localStorage.getItem(LS_AUDIT_KEY);
    const entries: AuditEntry[] = raw ? JSON.parse(raw) : [];
    entries.unshift(entry);
    localStorage.setItem(LS_AUDIT_KEY, JSON.stringify(entries.slice(0, 500)));
  } catch { /* ignore */ }
}

function loadAuditEntries(storageKey: string): AuditEntry[] {
  try {
    const raw = localStorage.getItem(LS_AUDIT_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as AuditEntry[]).filter(e => e.storageKey === storageKey);
  } catch { return []; }
}

// ─── Discharge Gates Definitions ─────────────────────────────────────────────
type GateDef = Omit<DischargeGate, 'status' | 'notes' | 'completedAt' | 'operator' | 'operatorId' | 'operatorTimestamp'>;

const AU_GATES: GateDef[] = [
  {
    id: 'au_discharge_licence',
    title: 'Discharge Licence / Environment Protection Notice (EPN)',
    description: 'Obtain a valid EPA discharge licence or Environment Protection Notice for the specific discharge point and waste category. Confirm licence number and conditions are current.',
    reference: 'EP Act 2017 (Vic) / Protection of the Environment Operations Act 1997 (NSW) / Environmental Protection Act 1994 (Qld)',
    jurisdiction: 'AU',
    required: true,
  },
  {
    id: 'au_discharge_category',
    title: 'Discharge Category Classification',
    description: 'Classify effluent as Category 1 (low-risk, conditional), Category 2 (moderate-risk, licence required) or Category 3 (high-risk, strict conditions). Document basis for classification.',
    reference: 'ANZECC/ARMCANZ 2000 Water Quality Guidelines — Section 4.2',
    jurisdiction: 'AU',
    required: true,
  },
  {
    id: 'au_reuse_feasibility',
    title: 'Reuse Feasibility Study',
    description: 'Assess whether treated effluent can be reused (irrigation, process water, industrial). Prepare feasibility study in accordance with WSAA Recycled Water Quality Management Framework where reuse is viable.',
    reference: 'WSAA Recycled Water Quality Management — WSA 306-2014',
    jurisdiction: 'AU',
    required: false,
  },
  {
    id: 'au_discharge_management_plan',
    title: 'Discharge Management Plan (DMP)',
    description: 'Prepare and approve a site-specific Discharge Management Plan covering discharge volumes, monitoring schedule, contingency procedures, and responsible officer details.',
    reference: 'EPA Victoria Best Practice Environmental Management Guidelines',
    jurisdiction: 'AU',
    required: true,
  },
  {
    id: 'au_ambient_monitoring',
    title: 'Ambient Monitoring Report (13 Parameters)',
    description: 'Conduct and document ambient monitoring for all 13 required parameters: pH, TSS, BOD₅, COD, TN, TP, NH₃-N, Oil & Grease, Conductivity, Lead (Pb), Chromium (Cr), Cadmium (Cd), Temperature.',
    reference: 'ANZECC/ARMCANZ 2000 Water Quality Guidelines — Schedule B1',
    jurisdiction: 'AU',
    required: true,
  },
  {
    id: 'au_mixing_zone',
    title: 'Mixing Zone Assessment',
    description: 'Assess and document the mixing zone for the receiving water body. Define the boundary within which dilution reduces concentrations to guideline levels. Submit to EPA for approval where required.',
    reference: 'ANZECC/ARMCANZ 2000 — Section 3.4 Mixing Zones',
    jurisdiction: 'AU',
    required: false,
  },
];

const UK_GATES: GateDef[] = [
  {
    id: 'uk_environmental_permit',
    title: 'Environmental Permit or Trade Effluent Consent',
    description: 'Obtain an Environmental Permit from the Environment Agency (for direct discharge to controlled waters) OR a Trade Effluent Consent from the sewerage undertaker (Severn Trent, Thames Water, etc.) for discharge to sewer. Both cannot substitute for each other.',
    reference: 'Environmental Permitting (England & Wales) Regulations 2016 / Water Industry Act 1991 s118',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_specific_substances',
    title: 'Specific Substances Assessment',
    description: 'Assess effluent for List I and List II dangerous substances as defined under the Dangerous Substances Directive (76/464/EEC). Confirm concentrations are below Environmental Quality Standards (EQS). Document substance-specific controls.',
    reference: 'Environmental Permitting Regulations 2016, Sch. 22 — Dangerous Substances Directive 76/464/EEC',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_design_manufacturing_standards',
    title: 'Design & Manufacturing Standards Compliance',
    description: 'Confirm all treatment equipment complies with applicable BS EN / BS standards. Document CE marking, UKCA marking, or equivalent. Key standards: BS EN 1825 (grease separators), BS EN 12255 series (wastewater treatment), BS 6920 (materials in contact with water).',
    reference: 'BS EN 12255 series / BS EN 1825 / BS 6920 / WIMES technical specifications',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_maintenance_records',
    title: 'Maintenance Records & Service Schedule',
    description: 'Establish and maintain a documented maintenance schedule for all treatment plant components. Records must include service dates, contractor details, parts replaced, and performance test results. Minimum 3-year retention.',
    reference: 'EA Technical Guidance Note — Trade Effluent Consents (January 2020)',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_desludging_records',
    title: 'Desludging & Waste Removal Records',
    description: 'Maintain records of all sludge removal events: date, volume, receiving facility, waste carrier licence number, and duty-of-care transfer notes. Confirm sludge is removed only by licensed waste carriers.',
    reference: 'Environmental Protection Act 1990 s34 Duty of Care / Waste (England & Wales) Regulations 2011',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_authorised_contractors',
    title: 'Waste Removal by Authorised Contractors',
    description: 'Verify that all waste streams (screenings, sludge, chemical containers) are collected and transported only by contractors registered with the Environment Agency as licensed waste carriers. Keep carrier licence copies on file.',
    reference: 'Environmental Protection Act 1990 / Controlled Waste (Registration of Carriers and Seizure of Vehicles) Regulations 1991',
    jurisdiction: 'UK',
    required: true,
  },
  {
    id: 'uk_public_consultation_closure',
    title: 'Public Consultation Closed (where applicable)',
    description: 'For new or modified Environmental Permits, confirm that the statutory public consultation period (minimum 20 working days) has closed and all objections have been addressed or accepted by the EA. Not required for standard Trade Effluent Consents.',
    reference: 'Environmental Permitting Regulations 2016 Reg. 25 — Public Participation',
    jurisdiction: 'UK',
    required: false,
  },
];

const EU_GATES: GateDef[] = [
  {
    id: 'eu_uwwtd_authorization',
    title: 'Urban Wastewater Treatment Authorisation',
    description: 'Obtain formal authorisation from the competent authority for discharge from an urban wastewater treatment plant. Authorisation must specify treatment standard (secondary or more stringent), design capacity, and discharge point. Update required for any material change to the facility.',
    reference: 'UWWTD 91/271/EEC Article 12 / Council Directive 91/271/EEC as amended by Directive 98/15/EC',
    jurisdiction: 'EU',
    required: true,
  },
  {
    id: 'eu_uwwtd_secondary_treatment',
    title: 'Secondary Treatment Performance Standard Met',
    description: 'Confirm treatment achieves secondary treatment requirements: BOD₅ ≤ 20 mg/L (or ≥70% reduction), COD ≤ 125 mg/L (or ≥75% reduction), TSS ≤ 35 mg/L (or ≥90% reduction for population >10,000). Document and retain performance data for competent authority inspection.',
    reference: 'UWWTD 91/271/EEC Annex I, Table 1 — Secondary Treatment Performance Requirements',
    jurisdiction: 'EU',
    required: true,
  },
  {
    id: 'eu_uwwtd_nutrient_removal',
    title: 'Nutrient Removal — Sensitive Area Compliance',
    description: 'For discharges to sensitive areas and their catchments, confirm nitrogen and/or phosphorus removal targets are met: Total P ≤ 1–2 mg/L (pop. > 100,000) or ≤ 2 mg/L (pop. 10,000–100,000), Total N ≤ 10–15 mg/L. Confirm if discharge falls within a Member State-designated sensitive area.',
    reference: 'UWWTD 91/271/EEC Article 5 & Annex I, Table 2 — Sensitive Area Nutrient Requirements',
    jurisdiction: 'EU',
    required: false,
  },
  {
    id: 'eu_uwwtd_monitoring_plan',
    title: 'Monitoring Programme & Competent Authority Reporting',
    description: 'Implement and maintain the required monitoring programme for influent and effluent per Annex I Table 3. Submit annual performance data to the competent authority. Confirm Member State reporting obligations to the European Commission have been met. Retain monitoring records for minimum 5 years.',
    reference: 'UWWTD 91/271/EEC Article 15 & Annex I, Table 3 — Monitoring Requirements',
    jurisdiction: 'EU',
    required: true,
  },
  {
    id: 'eu_uwwtd_sludge_management',
    title: 'Sludge Management & Disposal Compliance',
    description: 'Prepare and maintain a sludge management plan. Confirm sludge re-use and disposal complies with Sewage Sludge Directive 86/278/EEC for agricultural land application. Ensure pre-treatment sludge from exclusively industrial sources is handled separately where required.',
    reference: 'UWWTD 91/271/EEC Article 14 / Sewage Sludge Directive 86/278/EEC',
    jurisdiction: 'EU',
    required: true,
  },
  {
    id: 'eu_uwwtd_industrial_pretreatment',
    title: 'Industrial Pre-Treatment Assessment',
    description: 'Assess whether industrial effluent entering the collecting system requires pre-treatment prior to discharge. Where required, confirm pre-treatment regulations and discharge agreements are in place. Document basis for any industrial discharge exemptions. Check compliance with Annex I requirements for industrial discharges.',
    reference: 'UWWTD 91/271/EEC Article 11 — Industrial Waste Water Pre-Treatment',
    jurisdiction: 'EU',
    required: false,
  },
  {
    id: 'eu_uwwtd_public_participation',
    title: 'Public Participation & Transparency',
    description: 'Confirm that public participation requirements have been met during plan development and modifications. Wastewater treatment plans and data must be made publicly available. For Member States implementing the revised UWWTD (2022 recast), confirm extended energy neutrality and micro-pollutant obligations are being assessed.',
    reference: 'UWWTD 91/271/EEC Article 16 / Revised UWWTD Proposal COM(2022) 541 — Articles 19–20',
    jurisdiction: 'EU',
    required: false,
  },
];

const US_EPA_GATES: GateDef[] = [
  {
    id: 'us_npdes_permit',
    title: 'NPDES Permit — Obtained & Current',
    description: 'Confirm a valid NPDES permit (individual or general) has been obtained from EPA Region or delegated State agency. Permit must specify all discharge points, pollutant limits (ELGs and/or water quality-based), monitoring frequency, and reporting obligations. Confirm permit is within its 5-year cycle or renewal has been submitted (permit shield applies during timely renewal).',
    reference: 'Clean Water Act (CWA) 33 U.S.C. §1342 / 40 CFR Parts 122–125 — NPDES Permit Program',
    jurisdiction: 'US',
    required: true,
  },
  {
    id: 'us_npdes_tbels',
    title: 'Technology-Based Effluent Limits (TBELs) Compliance',
    description: 'Document compliance with technology-based effluent guidelines (ELGs) applicable to the facility\'s industrial category (40 CFR Subchapter N). For POTWs, confirm secondary treatment standards are met: BOD₅ ≤ 30 mg/L, TSS ≤ 30 mg/L, pH 6–9, minimum 85% removal of BOD₅ and TSS. Retain effluent monitoring data for minimum 3 years.',
    reference: '40 CFR Parts 405–471 — Effluent Limitations Guidelines / 40 CFR Part 133 — Secondary Treatment Regulation',
    jurisdiction: 'US',
    required: true,
  },
  {
    id: 'us_npdes_wqbels',
    title: 'Water Quality-Based Effluent Limits (WQBELs) Analysis',
    description: 'Assess whether Water Quality-Based Effluent Limits have been established in the permit. Where WQBELs apply, confirm effluent meets limits derived from applicable Water Quality Standards including designated uses, criteria, and anti-degradation requirements. Document any mixing zone assumptions and reasonable potential analysis (RPA) findings.',
    reference: '40 CFR Part 131 — Water Quality Standards / 40 CFR 122.44(d) — WQBELs / EPA RPA Guidance',
    jurisdiction: 'US',
    required: true,
  },
  {
    id: 'us_npdes_dmr',
    title: 'Discharge Monitoring Report (DMR) — Submitted on Time',
    description: 'Confirm all required Discharge Monitoring Reports have been submitted via NetDMR (or state equivalent) by permit-specified due dates. DMRs must report all monitoring data, even where no discharge occurred ("no-discharge" reporting). Retain copies of all submitted DMRs and supporting lab data for minimum 3 years from reporting date.',
    reference: '40 CFR 122.41(l)(4) — DMR Submission / 40 CFR 122.21(l)(4) — NetDMR Requirement',
    jurisdiction: 'US',
    required: true,
  },
  {
    id: 'us_npdes_pretreatment',
    title: 'Pre-Treatment Programme (Industrial Users)',
    description: 'For POTWs with a local pre-treatment programme: confirm all Significant Industrial Users (SIUs) are identified, permitted, and monitored. For industrial dischargers to POTW: confirm compliance with applicable categorical pre-treatment standards (40 CFR Subchapter N) or local limits established by the POTW. Retain inspection and monitoring records.',
    reference: '40 CFR Part 403 — Pre-Treatment Standards / CWA §307(b) — Pre-Treatment Requirements',
    jurisdiction: 'US',
    required: false,
  },
  {
    id: 'us_npdes_antidegradation',
    title: 'Anti-Degradation Assessment (40 CFR 131.12)',
    description: 'Confirm anti-degradation assessment has been completed for any new or increased discharge to High Quality Waters (Tier 2) or Outstanding National Resource Waters (Tier 3). Document that any increase in loading has been justified through the applicable socioeconomic analysis required by the State\'s anti-degradation policy.',
    reference: '40 CFR 131.12 — Anti-Degradation Policy / EPA Anti-Degradation Implementation Guidance (2015)',
    jurisdiction: 'US',
    required: false,
  },
  {
    id: 'us_npdes_spill_response',
    title: 'Spill Prevention, Control & Emergency Response Plan',
    description: 'Maintain an up-to-date Spill Prevention, Control, and Countermeasure (SPCC) Plan if at or above threshold oil storage volumes. Confirm an Emergency Response Plan exists for unpermitted discharges and is communicated to staff. Document any reportable discharges reported to the National Response Center (NRC) within 24 hours as required.',
    reference: '40 CFR Part 112 — SPCC Plan / CWA §311 — Oil & Hazardous Substance Liability / NRC: 1-800-424-8802',
    jurisdiction: 'US',
    required: true,
  },
];

// ─── Gate helpers ─────────────────────────────────────────────────────────────
type JurisdictionKey = 'AU' | 'UK' | 'EU' | 'US';

const STD_TO_JURISDICTION: Record<string, JurisdictionKey> = {
  AU_EPA: 'AU', UK_EA: 'UK', EU_UWWTD: 'EU', US_EPA: 'US',
};

function buildDefaultGates(j: JurisdictionKey): DischargeGate[] {
  const defs = j === 'AU' ? AU_GATES
             : j === 'UK' ? UK_GATES
             : j === 'EU' ? EU_GATES
             : j === 'US' ? US_EPA_GATES
             : UK_GATES;
  return defs.map(d => ({ ...d, status: 'incomplete' as GateStatus, notes: '' }));
}

function loadGates(key: string): DischargeGate[] {
  try {
    const raw = localStorage.getItem(LS_GATES_KEY);
    if (!raw) return [];
    const all: Record<string, DischargeGate[]> = JSON.parse(raw);
    return all[key] || [];
  } catch { return []; }
}

function saveGates(key: string, gates: DischargeGate[]) {
  try {
    const raw = localStorage.getItem(LS_GATES_KEY);
    const all: Record<string, DischargeGate[]> = raw ? JSON.parse(raw) : {};
    all[key] = gates;
    localStorage.setItem(LS_GATES_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

// ─── Gate Card ────────────────────────────────────────────────────────────────
function GateCard({
  gate,
  onUpdate,
}: {
  gate: DischargeGate;
  onUpdate: (id: string, status: GateStatus, notes: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localNotes, setLocalNotes] = useState(gate.notes);

  const statusCfg = {
    complete:   { icon: CheckSquare,   color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'COMPLETE' },
    incomplete: { icon: Square,        color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   label: 'PENDING'  },
    na:         { icon: XCircle,       color: 'text-slate-500',   bg: 'bg-slate-800/40',   border: 'border-slate-800/40',   label: 'N/A'      },
  };
  const sc = statusCfg[gate.status];
  const StatusIcon = sc.icon;

  const cycle = () => {
    const next: GateStatus = gate.status === 'incomplete' ? 'complete'
                           : gate.status === 'complete'   ? 'na'
                           : 'incomplete';
    onUpdate(gate.id, next, localNotes);
  };

  const jurisdictionBadge =
    gate.jurisdiction === 'AU' ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold">🇦🇺 AU</span>
    : gate.jurisdiction === 'UK' ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-sky-500/10 text-sky-400 border border-sky-500/20 font-bold">🇬🇧 UK</span>
    : gate.jurisdiction === 'EU' ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-violet-500/10 text-violet-400 border border-violet-500/20 font-bold">🇪🇺 EU</span>
    : <span className="px-1.5 py-0.5 rounded text-[10px] bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold">🇺🇸 US</span>;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${open ? sc.bg : 'bg-rb-surface'} ${sc.border}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-rb-surface/5 transition-colors" onClick={() => setOpen(o => !o)}>
        <button
          onClick={(e) => { e.stopPropagation(); cycle(); }}
          className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border ${sc.bg} ${sc.border} hover:opacity-80 transition-opacity`}
          title="Click to cycle: Pending → Complete → N/A"
        >
          <StatusIcon className={`w-3.5 h-3.5 ${sc.color}`} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {jurisdictionBadge}
            {gate.required && <span className="px-1.5 py-0.5 rounded text-[10px] bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold">REQUIRED</span>}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.color} ${sc.border}`}>{sc.label}</span>
          </div>
          <p className="text-xs font-bold text-white mt-0.5 truncate">{gate.title}</p>
          {gate.operator && gate.operatorTimestamp && (
            <p className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5 shrink-0" />
              {gate.operator} · {new Date(gate.operatorTimestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {open ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-800/50">
          <p className="text-[11px] text-slate-400 mt-2">{gate.description}</p>
          <div className="flex items-start gap-1.5 px-2 py-1.5 rounded bg-slate-800/50 border border-slate-800/50">
            <FileText className="w-3 h-3 text-slate-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-slate-500 font-mono">{gate.reference}</p>
          </div>
          <textarea
            value={localNotes}
            onChange={e => setLocalNotes(e.target.value)}
            onBlur={() => onUpdate(gate.id, gate.status, localNotes)}
            placeholder="Add notes, reference numbers, permit details, responsible officer…"
            rows={2}
            className="w-full px-2 py-1.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-[11px] text-slate-300 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40 resize-none"
          />
          <div className="flex gap-2">
            {(['complete', 'incomplete', 'na'] as GateStatus[]).map(s => {
              const cfg = statusCfg[s];
              const Ic = cfg.icon;
              return (
                <button
                  key={s}
                  onClick={() => onUpdate(gate.id, s, localNotes)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                    gate.status === s ? `${cfg.bg} ${cfg.color} ${cfg.border}` : 'bg-slate-800/40 text-slate-500 border-slate-800/40 hover:border-slate-600'
                  }`}
                >
                  <Ic className="w-3 h-3" />{cfg.label}
                </button>
              );
            })}
          </div>
          {gate.operator && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-slate-800/30 border border-slate-800/30 text-[10px] text-slate-500">
              <User className="w-3 h-3 shrink-0" />
              Last updated by <span className="text-slate-400 font-medium mx-1">{gate.operator}</span>
              {gate.operatorTimestamp && (
                <span>{new Date(gate.operatorTimestamp).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Discharge Certificate Print Modal ───────────────────────────────────────
function GateCertificateModal({ gates, standard, storageKey, onClose }: {
  gates: DischargeGate[];
  standard: string;
  storageKey: string;
  onClose: () => void;
}) {
  const now = new Date();
  const stats = {
    complete: gates.filter(g => g.status === 'complete').length,
    na: gates.filter(g => g.status === 'na').length,
    pending: gates.filter(g => g.status === 'incomplete').length,
    required: gates.filter(g => g.required).length,
    requiredComplete: gates.filter(g => g.required && g.status === 'complete').length,
  };
  const allRequiredComplete = stats.required === stats.requiredComplete;

  const stdLabel = standard === 'UK_EA' ? 'Environment Agency — Environmental Permitting (England & Wales) Regulations 2016'
    : standard === 'AU_EPA' ? 'Australian EPA — ANZECC/ARMCANZ 2000 Water Quality Guidelines'
    : standard === 'EU_UWWTD' ? 'EU — Urban Wastewater Treatment Directive 91/271/EEC'
    : standard === 'US_EPA' ? 'US EPA — NPDES / Clean Water Act 33 U.S.C. §1342'
    : standard;

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Discharge Gate Certificate — ${standard} — ${now.toLocaleDateString('en-GB')}`;
    window.print();
    document.title = originalTitle;
  };

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body * { visibility: hidden; }
          #gate-cert-content, #gate-cert-content * { visibility: visible; }
          #gate-cert-content { position: absolute; left: 0; top: 0; width: 100%; background: white !important; color: black !important; font-size: 8pt !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/70 p-4">
        <div className="relative w-full max-w-[860px] bg-rb-surface rounded-xl shadow-2xl my-8">
          <div className="no-print flex items-center justify-between p-4 border-b border-slate-800/60 bg-rb-elevated rounded-t-xl">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-400">Discharge Gate Certificate — Print Preview</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handlePrint} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                <Printer className="w-4 h-4" /> Print / Save PDF
              </button>
              <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-400 hover:bg-rb-elevated transition-colors">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div id="gate-cert-content" className="p-8 bg-rb-surface text-slate-200">
            {/* Header */}
            <div className="border-2 border-slate-800 mb-5">
              <div className="flex items-stretch border-b border-slate-800">
                <div className="flex-1 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Reynolds &amp; Bauhm Ltd</p>
                  <p className="text-[10px] text-slate-400 mb-2">Wastewater Treatment Engineering · Environmental Solutions</p>
                  <h1 className="text-base font-black text-slate-200">Discharge Validation Gate Certificate</h1>
                  <p className="text-xs text-slate-600 mt-0.5">{stdLabel}</p>
                </div>
                <div className="w-44 shrink-0 border-l border-slate-800 bg-slate-900 text-white flex flex-col divide-y divide-slate-700">
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Status</p>
                    <p className={`text-[11px] font-black ${allRequiredComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {allRequiredComplete ? '✓ APPROVED' : '⚠ PENDING'}
                    </p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Issued</p>
                    <p className="text-[11px] font-semibold text-white">{now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Gates</p>
                    <p className="text-[11px] font-bold text-white">{stats.complete + stats.na}/{gates.length} actioned</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 divide-x divide-slate-800 bg-rb-elevated">
                {[
                  { label: 'Required', value: stats.required },
                  { label: 'Req. Complete', value: stats.requiredComplete },
                  { label: 'N/A', value: stats.na },
                  { label: 'Pending', value: stats.pending },
                ].map(s => (
                  <div key={s.label} className="px-3 py-2 text-center">
                    <p className="text-base font-black text-slate-300">{s.value}</p>
                    <p className="text-[10px] uppercase text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Gate table */}
            <h3 className="text-xs font-bold text-slate-600 uppercase mb-2 border-b border-slate-800/60 pb-1">Gate Checklist</h3>
            <table className="w-full text-xs border-collapse mb-5">
              <thead>
                <tr className="bg-rb-elevated">
                  <th className="text-left p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[35%]">Gate / Requirement</th>
                  <th className="text-center p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[10%]">Req'd</th>
                  <th className="text-center p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[12%]">Status</th>
                  <th className="text-left p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[20%]">Operator / Date</th>
                  <th className="text-left p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[23%]">Notes</th>
                </tr>
              </thead>
              <tbody>
                {gates.map(g => (
                  <tr key={g.id} className={g.status === 'complete' ? 'bg-emerald-500/10' : g.status === 'na' ? 'bg-rb-elevated' : ''}>
                    <td className="p-1.5 border border-slate-800/60 font-medium text-slate-300">{g.title}</td>
                    <td className="p-1.5 border border-slate-800/60 text-center">{g.required ? '✓' : '—'}</td>
                    <td className="p-1.5 border border-slate-800/60 text-center font-bold">
                      <span className={g.status === 'complete' ? 'text-emerald-400' : g.status === 'na' ? 'text-slate-500' : 'text-amber-400'}>
                        {g.status === 'complete' ? 'COMPLETE' : g.status === 'na' ? 'N/A' : 'PENDING'}
                      </span>
                    </td>
                    <td className="p-1.5 border border-slate-800/60 text-slate-600">
                      {g.operator ? `${g.operator}${g.operatorTimestamp ? ' · ' + new Date(g.operatorTimestamp).toLocaleDateString('en-GB') : ''}` : '—'}
                    </td>
                    <td className="p-1.5 border border-slate-800/60 text-slate-600">{g.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Footer */}
            <div className="border-t-2 border-slate-800 pt-2 flex items-center justify-between mt-4">
              <p className="text-[9px] text-slate-500">Reynolds &amp; Bauhm Ltd · Discharge Validation Certificate · This document is for internal regulatory management purposes only.</p>
              <p className="text-[9px] font-mono font-bold text-slate-300">{now.toISOString().split('T')[0]}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Discharge Gates Tab ──────────────────────────────────────────────────────
function DischargeGatesTab({ standard, customerId }: { standard: string; customerId?: string }) {
  const { user } = useAuth();

  const defaultJurisdiction: JurisdictionKey = STD_TO_JURISDICTION[standard] || 'UK';
  const storageKey = `${standard}_${customerId || 'global'}`;

  const [activeJurisdiction, setActiveJurisdiction] = useState<JurisdictionKey>(defaultJurisdiction);
  const [gates, setGates] = useState<DischargeGate[]>(() => {
    const saved = loadGates(storageKey);
    if (saved.length > 0) return saved;
    return buildDefaultGates(defaultJurisdiction);
  });
  const [showAudit, setShowAudit] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);

  useEffect(() => {
    setAuditEntries(loadAuditEntries(storageKey));
  }, [storageKey]);

  const operatorName = user
    ? ((user as any).name || (user as any).username || user.email || 'System')
    : 'Anonymous';
  const operatorId = user ? String(user.id ?? 0) : '0';

  const handleUpdate = useCallback((id: string, status: GateStatus, notes: string) => {
    setGates(prev => {
      const prevGate = prev.find(g => g.id === id);
      const now = new Date().toISOString();
      const next = prev.map(g =>
        g.id === id
          ? { ...g, status, notes, completedAt: status === 'complete' ? now : undefined,
              operator: operatorName, operatorId, operatorTimestamp: now }
          : g
      );
      saveGates(storageKey, next);

      // Append audit entry
      if (prevGate && prevGate.status !== status) {
        const entry: AuditEntry = {
          gateId: id,
          gateTitle: prevGate.title,
          storageKey,
          fromStatus: prevGate.status,
          toStatus: status,
          operator: operatorName,
          operatorId,
          timestamp: now,
        };
        appendAuditEntry(entry);
        setAuditEntries(prev => [entry, ...prev].slice(0, 50));
      }
      return next;
    });
    if (status === 'complete') toast.success('Gate marked complete');
  }, [storageKey, operatorName, operatorId]);

  const handleLoadJurisdiction = (j: JurisdictionKey) => {
    setActiveJurisdiction(j);
    const fresh = buildDefaultGates(j);
    setGates(fresh);
    saveGates(storageKey, fresh);
    toast.info(`Loaded ${j} regulatory gate set`);
  };

  const handleReset = () => {
    if (!confirm('Reset all gates to Pending? This clears all notes, completion status, and operator records.')) return;
    const fresh = buildDefaultGates(activeJurisdiction);
    setGates(fresh);
    saveGates(storageKey, fresh);
    toast.info('Discharge gates reset');
  };

  const stats = {
    total: gates.length,
    complete: gates.filter(g => g.status === 'complete').length,
    pending: gates.filter(g => g.status === 'incomplete').length,
    na: gates.filter(g => g.status === 'na').length,
    required: gates.filter(g => g.required).length,
    requiredComplete: gates.filter(g => g.required && g.status === 'complete').length,
  };

  const allRequiredComplete = stats.required === stats.requiredComplete && stats.required > 0;
  const pct = stats.total > 0 ? Math.round((stats.complete + stats.na) / stats.total * 100) : 0;

  const JURISDICTION_BTNS: { key: JurisdictionKey; flag: string; label: string; color: string; activeCls: string }[] = [
    { key: 'UK', flag: '🇬🇧', label: 'United Kingdom', color: 'bg-sky-500/15 text-sky-400 border-sky-500/30',     activeCls: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
    { key: 'AU', flag: '🇦🇺', label: 'Australia',      color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', activeCls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    { key: 'EU', flag: '🇪🇺', label: 'EU (UWWTD)',     color: 'bg-violet-500/15 text-violet-400 border-violet-500/30', activeCls: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
    { key: 'US', flag: '🇺🇸', label: 'USA (NPDES)',    color: 'bg-rose-500/15 text-rose-400 border-rose-500/30',   activeCls: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
  ];

  return (
    <div className="space-y-4">
      {/* Jurisdiction Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0">Regulatory Jurisdiction:</span>
        {JURISDICTION_BTNS.map(btn => (
          <button
            key={btn.key}
            onClick={() => handleLoadJurisdiction(btn.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
              activeJurisdiction === btn.key
                ? btn.activeCls
                : 'bg-slate-800/40 text-slate-500 border-slate-800/40 hover:border-slate-600'
            }`}
          >
            {btn.flag} {btn.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowCertificate(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-rb-brand border border-rb-brand/30 bg-rb-brand/10 hover:bg-rb-brand/20 transition-all"
          >
            <Award className="w-3 h-3" /> Export Certificate
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-slate-500 border border-slate-800/40 hover:border-slate-600 hover:text-slate-300 transition-all"
          >
            <SlidersHorizontal className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>

      {/* Operator identity notice */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-800/30 text-[10px]">
        <User className="w-3 h-3 text-slate-500 shrink-0" />
        <span className="text-slate-500">Gate actions recorded as:</span>
        <span className="text-slate-300 font-bold">{operatorName}</span>
        {!user && <span className="text-amber-400 ml-1">(sign in for named audit trail)</span>}
      </div>

      {/* Progress Banner */}
      <div className={`rounded-xl p-4 border ${allRequiredComplete ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/20'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {allRequiredComplete
              ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              : <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            }
            <div>
              <p className={`text-sm font-bold ${allRequiredComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
                {allRequiredComplete ? 'All required discharge gates complete' : 'Discharge validation pending'}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {stats.requiredComplete} of {stats.required} required gates complete · {pct}% overall progress
              </p>
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border flex items-center gap-1.5 ${
            allRequiredComplete ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
          }`}>
            {allRequiredComplete ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            {allRequiredComplete ? 'DISCHARGE APPROVED' : 'BLOCKED'}
          </div>
        </div>
        <div className="mt-3 h-1.5 bg-slate-800/60 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${allRequiredComplete ? 'bg-emerald-500' : 'bg-amber-500'}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Total', value: stats.total, color: 'text-white', bg: 'bg-slate-800' },
          { label: 'Complete', value: stats.complete, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
          { label: 'N/A', value: stats.na, color: 'text-slate-500', bg: 'bg-slate-800/40 border-slate-800/40' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg p-2.5 text-center border ${s.bg}`}>
            <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-500 uppercase">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Gate list */}
      <div className="space-y-2">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          {activeJurisdiction === 'AU' ? '🇦🇺 Australian Discharge Gates'
           : activeJurisdiction === 'UK' ? '🇬🇧 UK Discharge Gates'
           : activeJurisdiction === 'EU' ? '🇪🇺 EU UWWTD Discharge Gates'
           : '🇺🇸 US EPA NPDES Discharge Gates'} — click status icon to cycle
        </p>
        {gates.map(gate => (
          <GateCard key={gate.id} gate={gate} onUpdate={handleUpdate} />
        ))}
      </div>

      {/* Audit Trail */}
      <div className="overflow-hidden rb-card">
        <button
          onClick={() => setShowAudit(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/20 transition-colors"
        >
          <div className="flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Audit Trail</span>
            {auditEntries.length > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-400 text-[10px] font-bold">{auditEntries.length}</span>
            )}
          </div>
          {showAudit ? <ChevronUp className="w-3.5 h-3.5 text-slate-600" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-600" />}
        </button>
        {showAudit && (
          <div className="border-t border-slate-800/60 px-4 pb-3">
            {auditEntries.length === 0 ? (
              <p className="text-[10px] text-slate-600 py-3 text-center italic">No gate changes recorded yet — all status changes will appear here.</p>
            ) : (
              <div className="space-y-1 mt-3 max-h-60 overflow-y-auto">
                {auditEntries.slice(0, 20).map((e, i) => (
                  <div key={i} className="flex items-start gap-2 px-2 py-1.5 rounded bg-slate-800/30 border border-slate-800/30">
                    <Clock className="w-3 h-3 text-slate-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-slate-300 font-medium truncate">{e.gateTitle}</p>
                      <p className="text-[10px] text-slate-500">
                        <span className={`font-bold ${e.fromStatus === 'incomplete' ? 'text-amber-400' : e.fromStatus === 'complete' ? 'text-emerald-400' : 'text-slate-500'}`}>
                          {e.fromStatus.toUpperCase()}
                        </span>
                        {' → '}
                        <span className={`font-bold ${e.toStatus === 'complete' ? 'text-emerald-400' : e.toStatus === 'na' ? 'text-slate-400' : 'text-amber-400'}`}>
                          {e.toStatus.toUpperCase()}
                        </span>
                        {' · '}<span className="text-slate-400">{e.operator}</span>
                        {' · '}{new Date(e.timestamp).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-600 mt-2 italic">Audit entries persisted locally. For regulated submissions export the Gate Certificate above.</p>
          </div>
        )}
      </div>

      {/* Certificate Modal */}
      {showCertificate && (
        <GateCertificateModal
          gates={gates}
          standard={standard}
          storageKey={storageKey}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
}

// ─── Param display helpers ────────────────────────────────────────────────────
const PARAM_LABELS: Record<string, string> = {
  ph: 'pH', bod: 'BOD', bod5: 'BOD₅', cod: 'COD', tss: 'TSS',
  nh3: 'NH₃-N', nh3n: 'NH₃-N', og: 'O&G', oandg: 'O&G', oilgrease: 'O&G',
  temperature: 'Temp', tds: 'TDS', conductivity: 'Cond.',
  lead: 'Pb', chromium: 'Cr', tp: 'Total-P', tn: 'Total-N',
  flowRate: 'Flow', turbidity: 'Turbidity', hardness: 'Hardness',
};
const PARAM_UNITS: Record<string, string> = {
  ph: '', bod: 'mg/L', bod5: 'mg/L', cod: 'mg/L', tss: 'mg/L',
  nh3: 'mg/L', nh3n: 'mg/L', og: 'mg/L', oandg: 'mg/L', oilgrease: 'mg/L',
  temperature: '°C', tds: 'mg/L', conductivity: 'µS/cm',
  lead: 'mg/L', chromium: 'mg/L', tp: 'mg/L', tn: 'mg/L',
  flowRate: 'm³/d', turbidity: 'NTU', hardness: 'mg/L',
};

function stdBadgeClass(std: string) {
  if (std === 'UK_EA')    return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (std === 'AU_EPA')   return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (std === 'EU_UWWTD') return 'bg-violet-500/10 text-violet-400 border-violet-500/20';
  if (std === 'US_EPA')   return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-slate-700 text-slate-400 border-slate-600';
}

// ─── Samples Tab ──────────────────────────────────────────────────────────────
function SamplesTab() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { activeStandard, currentCustomer } = useSession();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | 'all'>('all');
  const [selectedSiteId, setSelectedSiteId] = useState<number | 'all'>('all');
  const [sites, setSites] = useState<{ id: number; name: string; customerId: number | null }[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Resolve the DB integer ID — serverId takes priority over local timestamp id
  const sessionCustId = currentCustomer
    ? ((currentCustomer as any).serverId
        ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : 0))
    : 0;

  useEffect(() => {
    // CONFIDENTIALITY GATE — load nothing if no active session customer
    if (!sessionCustId) {
      setSamples([]);
      setLoading(false);
      return;
    }

    // Show local customers as immediate fallback while server loads
    const localCust: Customer[] = (JSON.parse(localStorage.getItem('rb_customers') || '[]') as any[]).map((c: any) => ({
      id: Number(c.id),
      name: c.name || '',
      company: c.company ?? null,
    }));
    setCustomers(localCust);

    setLoading(true);
    // Filter local effluent samples to ONLY the current user AND session customer (prevent cross-user bleed)
    const currentUserId = user ? Number(user.id) : 0;
    const allLocal = getAllLocalSamples()
      .filter(s => s.sampleType === 'effluent')
      .filter(s => !currentUserId || !s.userId || s.userId === currentUserId)
      .filter(s => s.customerId != null ? Number(s.customerId) === sessionCustId : false);

    if (isAuthenticated && hasPhpBackend()) {
      Promise.all([
        apiListInfluentSamples(sessionCustId, { type: 'effluent' }).catch(() => [] as any[]),
        apiListCustomers().catch(() => [] as any[]),
        apiListCustomerSites().catch(() => [] as any[]),
      ]).then(([rows, custRows, siteRows]) => {
        // Server customers supersede local ones
        const serverCusts: Customer[] = (custRows || []).map((c: any) => ({ id: Number(c.id), name: c.name, company: c.company ?? null }));
        setCustomers([...serverCusts, ...localCust.filter(lc => !serverCusts.some(sc => String(sc.id) === String(lc.id)))]);

        const serverSites = (siteRows || []).map((s: any) => ({
          id: Number(s.id),
          name: s.name || '',
          customerId: (s.customerId ?? s.customer_id) != null ? Number(s.customerId ?? s.customer_id) : null,
        }));
        setSites(serverSites);

        const server = rows.map((r: any) => ({
          id: Number(r.id),
          customerId: (r.customerId ?? r.customer_id) != null ? Number(r.customerId ?? r.customer_id) : null,
          siteId: (r.siteId ?? r.site_id) != null ? Number(r.siteId ?? r.site_id) : null,
          sampleName: r.sampleName || r.sample_name,
          reportNumber: r.reportNumber || r.report_number || buildRegulatoryReportNumber('EFF', r.standard || 'UK_EA'),
          params: typeof r.params === 'string' ? JSON.parse(r.params) : (r.params || {}),
          standard: r.standard || 'UK_EA',
          sampleType: 'effluent' as const,
          linkedSampleId: r.linkedSampleId ?? null,
          createdAt: r.createdAt || r.created_at,
        }));
        const merged = [...server, ...allLocal.filter((ls: any) => !server.some((ss: any) => ss.id === ls.id))];
        setSamples(merged);
      }).catch(() => setSamples(allLocal as any[])).finally(() => setLoading(false));
    } else {
      setSamples(allLocal as any[]);
      setLoading(false);
    }
  }, [isAuthenticated, user, sessionCustId]);

  const filtered = useMemo(() => {
    let rows = samples;
    if (selectedCustomerId !== 'all') rows = rows.filter(s => s.customerId === selectedCustomerId);
    if (selectedSiteId !== 'all') rows = rows.filter(s => s.siteId === selectedSiteId);
    if (dateFrom) rows = rows.filter(s => new Date(s.createdAt) >= new Date(dateFrom));
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      rows = rows.filter(s => new Date(s.createdAt) <= to);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(s =>
        s.sampleName.toLowerCase().includes(q) ||
        s.reportNumber.toLowerCase().includes(q)
      );
    }
    return rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [samples, selectedCustomerId, selectedSiteId, dateFrom, dateTo, search]);

  // Compliance helper
  const getParamCompliance = (standard: string, key: string, val: number): 'red' | 'amber' | 'green' | 'neutral' => {
    const std = ALL_STANDARDS.find(s => s.code === standard);
    if (!std) return 'neutral';
    const limMap: Record<string, number | undefined> = {
      bod5: std.limits.bod5, bod: std.limits.bod5, cod: std.limits.cod,
      tss: std.limits.tss, nh3n: std.limits.ammoniaN, nh3: std.limits.ammoniaN,
      oilgrease: std.limits.oilGrease, oandg: std.limits.oilGrease, og: std.limits.oilGrease,
      tp: std.limits.totalPhosphorus, tn: std.limits.totalNitrogen,
      lead: std.limits.lead, chromium: std.limits.chromium,
      temperature: std.limits.temperature, tds: std.limits.tds,
    };
    const lim = limMap[key.toLowerCase()];
    if (lim === undefined) return 'neutral';
    const pct = val / lim;
    if (pct > 1) return 'red';
    if (pct > 0.8) return 'amber';
    return 'green';
  };

  // Resolve site name for PDF — look up from samples if available
  const getSiteLabel = (s: Sample) => {
    if (!s.siteId) return undefined;
    const localSites = (() => { try { return JSON.parse(localStorage.getItem('rb_customer_sites_v2') || '[]'); } catch { return []; } })();
    const site = localSites.find((ls: any) => String(ls.id) === String(s.siteId) || String(ls.localId) === String(s.siteId));
    return site ? (site.name || site.siteName || undefined) : undefined;
  };

  // Linked influent reference — resolve from local samples
  const getLinkedInfluentRef = (s: Sample) => {
    if (!s.linkedSampleId) return undefined;
    const allSamples: any[] = (() => { try { return JSON.parse(localStorage.getItem('rb_influent_samples_v2') || '[]'); } catch { return []; } })();
    const linked = allSamples.find((ls: any) => ls.id === s.linkedSampleId || ls.localId === s.linkedSampleId);
    return linked ? (linked.reportNumber || linked.report_number || `Sample ID: ${s.linkedSampleId}`) : `Sample ID: ${s.linkedSampleId}`;
  };

  // PDF handlers
  const handlePreview = (s: Sample) => {
    try {
      const url = generateEffluentSamplePDF(s.params, {
        reportNumber: s.reportNumber,
        sampleReferenceId: s.reportNumber,
        sampleName: s.sampleName,
        standard: s.standard,
        sampleDate: s.createdAt,
        preparedBy: 'Reynolds & Bauhm Ltd',
        siteName: getSiteLabel(s),
        linkedSampleId: s.linkedSampleId,
        linkedInfluentRef: getLinkedInfluentRef(s),
        isDafEffluent: s.sampleType === 'effluent',
      }, 'preview');
      if (url) setPreviewUrl(url);
    } catch (err: any) { toast.error(`PDF failed: ${err.message}`); }
  };

  const handleDownload = (s: Sample) => {
    try {
      generateEffluentSamplePDF(s.params, {
        reportNumber: s.reportNumber,
        sampleReferenceId: s.reportNumber,
        sampleName: s.sampleName,
        standard: s.standard,
        sampleDate: s.createdAt,
        preparedBy: 'Reynolds & Bauhm Ltd',
        siteName: getSiteLabel(s),
        linkedSampleId: s.linkedSampleId,
        linkedInfluentRef: getLinkedInfluentRef(s),
        isDafEffluent: s.sampleType === 'effluent',
      }, 'save');
      toast.success('PDF downloaded');
    } catch (err: any) { toast.error(`PDF failed: ${err.message}`); }
  };

  // Stat helpers
  const thisMonth = useMemo(() => {
    const now = new Date();
    return samples.filter(s => {
      const d = new Date(s.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [samples]);

  const linkedToSite = useMemo(() => samples.filter(s => s.siteId != null).length, [samples]);
  const linkedToInfluent = useMemo(() => samples.filter(s => s.linkedSampleId != null).length, [samples]);

  // CONFIDENTIALITY GATE UI — no active session customer
  if (!currentCustomer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-amber-500/20 bg-amber-500/5">
        <Building2 className="w-12 h-12 text-amber-400/40 mb-4" />
        <p className="text-base font-bold text-amber-400 mb-1">No active customer session</p>
        <p className="text-sm text-slate-500 text-center max-w-sm">
          Select a customer from the Customers page to view discharge records. Records are strictly scoped to the active session customer.
        </p>
        <button
          onClick={() => navigate('/customers')}
          className="mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all"
        >
          <Building2 className="w-4 h-4" /> Go to Customers
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <RegulatoryNotice standard={activeStandard} context="effluent" compact />

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Total Records', value: samples.length, color: 'amber' },
          { label: 'Linked to Site', value: linkedToSite, color: 'teal' },
          { label: 'Linked to Influent', value: linkedToInfluent, color: 'orange' },
          { label: 'This Month', value: thisMonth, color: 'amber' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`rounded-xl border border-${color}-500/20 bg-${color}-500/[0.03] px-3 py-2.5`}>
            <p className={`text-xl font-black text-${color}-400`}>{loading ? '—' : value}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search discharge records..."
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/40"
          />
        </div>
        <select
          value={selectedCustomerId}
          onChange={(e) => setSelectedCustomerId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
          className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-amber-500/40"
        >
          <option value="all">All Customers</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>
          ))}
        </select>
        {sites.length > 0 && (
          <select
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-amber-500/40"
          >
            <option value="all">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-amber-500/40"
          title="From date" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-amber-500/40"
          title="To date" />
        {(search || selectedCustomerId !== 'all' || selectedSiteId !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => { setSearch(''); setSelectedCustomerId('all'); setSelectedSiteId('all'); setDateFrom(''); setDateTo(''); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200 bg-slate-800/50 border border-slate-800/50 transition-all"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Flat expandable list */}
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-800/50 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.02] p-10 text-center">
          <Beaker className="w-10 h-10 text-amber-400/30 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No discharge records found</p>
          <p className="text-xs text-slate-600 mt-1">
            {search || selectedCustomerId !== 'all' || selectedSiteId !== 'all' ? 'Try clearing filters' : 'Add effluent samples to create discharge records'}
          </p>
          <button
            onClick={() => navigate('/effluent-entry')}
            className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors mx-auto"
          >
            <Plus className="w-3.5 h-3.5" /> Add Discharge Sample
          </button>
        </div>
      ) : (
        <div className="space-y-2 max-w-[1080px]">
          {filtered.map(s => {
            const isExpanded = expandedId === s.id;
            const sampleDate = s.createdAt ? new Date(s.createdAt) : null;
            const dateStr = sampleDate ? sampleDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
            const timeStr = sampleDate ? sampleDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
            const siteName = s.siteId ? (sites.find(st => st.id === s.siteId)?.name ?? `Site ${s.siteId}`) : null;
            const paramEntries = Object.entries(s.params || {});
            const compactParams = paramEntries.slice(0, 4);

            return (
              <motion.div
                key={s.id}
                layout
                className={`rounded-xl border overflow-hidden transition-all duration-200 ${
                  isExpanded
                    ? 'border-amber-500/40 bg-amber-500/[0.03] shadow-lg shadow-amber-500/5'
                    : 'border-slate-800/50 bg-[#0f1a0a]/60 hover:border-amber-500/25 hover:bg-amber-500/[0.02]'
                }`}
              >
                {/* Collapsed header — entire row clickable */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                  onClick={() => setExpandedId(isExpanded ? null : s.id)}
                >
                  {/* Amber icon */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    isExpanded ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-amber-500/10 border border-amber-500/20'
                  }`}>
                    <Beaker className="w-4 h-4 text-amber-400" />
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-mono text-amber-400 font-bold">{s.reportNumber}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/20 font-bold">DISCHARGE</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold ${stdBadgeClass(s.standard)}`}>{s.standard}</span>
                      {siteName && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/20 flex items-center gap-0.5 font-bold">
                          <MapPin className="w-2 h-2" />{siteName}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-white truncate mt-0.5">{s.sampleName}</p>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Calendar className="w-2.5 h-2.5" />{dateStr}{timeStr ? ` · ${timeStr}` : ''}
                      </span>
                      {s.linkedSampleId && (
                        <span className="text-[10px] text-teal-400 flex items-center gap-1">
                          <Droplets className="w-2.5 h-2.5" />Linked influent #{s.linkedSampleId}
                        </span>
                      )}
                      {compactParams.map(([k, v]) => {
                        const compliance = getParamCompliance(s.standard, k, v);
                        const chipColor = compliance === 'red'
                          ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : compliance === 'amber'
                          ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                          : compliance === 'green'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : 'bg-slate-800/50 text-slate-400 border-slate-800/50';
                        return (
                          <span key={k} className={`text-[9px] px-1.5 py-0.5 rounded border font-mono ${chipColor}`}>
                            {(PARAM_LABELS[k] || k).toUpperCase()}: {typeof v === 'number' ? v.toFixed(1) : v}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick actions (stop propagation) */}
                  <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handlePreview(s)}
                      title="Preview PDF"
                      className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDownload(s)}
                      title="Download PDF"
                      className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-amber-400' : ''}`} />
                </div>

                {/* Expanded detail */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-amber-500/20 px-4 pt-3 pb-4 space-y-3">

                        {/* Identity grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { label: 'Reference ID', value: s.reportNumber, mono: true },
                            { label: 'Standard', value: s.standard },
                            { label: 'Date & Time', value: `${dateStr}${timeStr ? ' · ' + timeStr : ''}` },
                            { label: 'Site', value: siteName || '—' },
                          ].map(({ label, value, mono }) => (
                            <div key={label} className="rounded-lg bg-slate-800/30 border border-slate-800/40 px-3 py-2">
                              <p className="text-[9px] text-slate-600 uppercase tracking-wider font-bold mb-0.5">{label}</p>
                              <p className={`text-xs font-bold text-white truncate ${mono ? 'font-mono text-amber-400' : ''}`}>{value}</p>
                            </div>
                          ))}
                        </div>

                        {/* All parameters with compliance coloring */}
                        {paramEntries.length > 0 && (
                          <div>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                              All Parameters ({paramEntries.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {paramEntries.map(([k, v]) => {
                                const compliance = getParamCompliance(s.standard, k, v);
                                const colorClass = compliance === 'red'
                                  ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                  : compliance === 'amber'
                                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                  : compliance === 'green'
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                  : 'bg-slate-800/50 text-slate-400 border-slate-800/50';
                                const unit = PARAM_UNITS[k.toLowerCase()] ?? '';
                                return (
                                  <span key={k} className={`text-[10px] px-2 py-1 rounded-lg border font-mono ${colorClass}`}>
                                    <span className="font-bold">{PARAM_LABELS[k.toLowerCase()] || k.toUpperCase()}</span>
                                    {': '}
                                    {typeof v === 'number' ? v.toFixed(2) : v}
                                    {unit ? ` ${unit}` : ''}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Linked influent info */}
                        {s.linkedSampleId && (
                          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-teal-500/20 bg-teal-500/5">
                            <div className="w-7 h-7 rounded-lg bg-teal-500/15 border border-teal-500/20 flex items-center justify-center shrink-0">
                              <Droplets className="w-3.5 h-3.5 text-teal-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-teal-400">Linked to Influent Sample #{s.linkedSampleId}</p>
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                This discharge record is paired with an influent sample — compliance gate comparison available.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* PDF action buttons */}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => handlePreview(s)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all"
                          >
                            <Eye className="w-3 h-3" /> Preview PDF
                          </button>
                          <button
                            onClick={() => handleDownload(s)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-all"
                          >
                            <Download className="w-3 h-3" /> Download PDF
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
          onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
        >
          <div
            className="bg-rb-surface rounded-2xl w-full max-w-4xl flex flex-col shadow-2xl"
            style={{ height: '90vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-amber-500/20 rounded-t-2xl shrink-0">
              <span className="text-sm font-bold text-white">Discharge Record — Regulatory PDF</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = previewUrl;
                    a.download = 'discharge-record.pdf';
                    a.click();
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button
                  onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full rounded-b-2xl" style={{ border: 'none' }} title="PDF Preview" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function EffluentPage() {
  const navigate = useNavigate();
  const { activeStandard, currentCustomer } = useSession();
  const [tab, setTab] = useState<'samples' | 'gates'>('samples');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Beaker}
        iconColor="text-amber-400"
        title="Discharge Records"
        subtitle="Treated discharge water — linked to influent by reference ID · compliance gate validation"
        actions={
          <>
            <button
              onClick={() => navigate('/effluent-entry')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-colors"
            >
              <Plus className="w-4 h-4" />Add Effluent Sample
            </button>
            <button
              onClick={() => navigate('/samples')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-slate-800/60 text-slate-300 border border-slate-800/60 hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />Back to Samples
            </button>
          </>
        }
      />

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-slate-800/60">
        <button
          onClick={() => setTab('samples')}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px transition-all ${
            tab === 'samples'
              ? 'border-rb-brand text-rb-brand rb-tab-active'
              : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
          }`}
        >
          <Beaker className="w-3.5 h-3.5" />Effluent Samples
        </button>
        <button
          onClick={() => setTab('gates')}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px transition-all ${
            tab === 'gates'
              ? 'border-emerald-500 text-emerald-400 rb-tab-active'
              : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
          }`}
        >
          <Shield className="w-3.5 h-3.5" />Discharge Validation Gates
          <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] border border-emerald-500/20 font-bold">
            {activeStandard === 'AU_EPA' ? '🇦🇺 AU · 6 gates'
             : activeStandard === 'EU_UWWTD' ? '🇪🇺 EU · 7 gates'
             : activeStandard === 'US_EPA' ? '🇺🇸 US · 7 gates'
             : '🇬🇧 UK · 7 gates'}
          </span>
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {tab === 'samples' ? (
          <motion.div key="samples" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <SamplesTab />
          </motion.div>
        ) : (
          <motion.div key="gates" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <DischargeGatesTab standard={activeStandard || 'UK_EA'} customerId={currentCustomer?.id} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
